import React, { useState, useEffect, useRef } from 'react';

const API = 'https://tcio-api.onrender.com';

const fmt$ = (n) => n == null ? '—' : '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtN = (n) => n == null ? '—' : parseInt(n).toLocaleString();

const AGE_ORDER = ['0-30', '31-60', '61-90', '91-120', '120+'];

const RISK_COLOR = (payer, aging) => {
  if (!payer) return '#94a3b8';
  const name = (payer || '').toUpperCase();
  if (name.includes('NORTH CAROLINA') || name.includes('MEDICA') || name.includes('MODA')) return '#ef4444';
  if (aging === '120+') return '#f97316';
  if (aging === '91-120') return '#f59e0b';
  return '#22c55e';
};

export default function AR({ token }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [activeSection, setActiveSection] = useState('action');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [piData, setPiData] = useState([]);
  const [piFilter, setPiFilter] = useState('');
  const fileRef = useRef();

  const headers = { Authorization: `Bearer ${token}` };
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/facilities`, { headers });
        const j = await r.json();
        if (j.ok) { setFacilities(j.data); setFacilityId(prev => prev || ((j.data[0] || {}).id || '')); }
      } catch (e) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ar/summary?facilityId=${facilityId}`, { headers });
      const d = await r.json();
      if (d.ok) setSummary(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadPI = async () => {
    try {
      const r = await fetch(`${API}/api/ar/payer-intelligence`, { headers });
      const d = await r.json();
      if (d.ok) setPiData(d.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadSummary(); loadPI(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!facilityId) { setUploadMsg({ ok: false, text: 'Select a facility before uploading' }); return; }
    setUploading(true);
    setUploadMsg(null);
    const form = new FormData();
    form.append('ar_csv', file);
    form.append('facility_id', facilityId);
    try {
      const r = await fetch(`${API}/api/ar/upload`, { method: 'POST', headers, body: form });
      const d = await r.json();
      if (d.ok) {
        setUploadMsg({ ok: true, text: `✅ Uploaded ${fmtN(d.summary.total_claims)} claims — ${fmt$(d.summary.open_billed)} open AR` });
        await loadSummary();
      } else {
        setUploadMsg({ ok: false, text: '❌ Upload failed: ' + d.error });
      }
    } catch (e) {
      setUploadMsg({ ok: false, text: '❌ ' + e.message });
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleAiAnalyze = async () => {
  if (!summary) return;
  setAiLoading(true);
  setAiAnalysis(null);
  try {
    const r = await fetch(`${API}/api/ar/analyze`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const d = await r.json();
    if (d.ok) {
      setAiAnalysis(d.analysis);
    } else {
      setAiAnalysis('Error: ' + d.error);
    }
  } catch (e) {
    setAiAnalysis('Error: ' + e.message);
  }
  setAiLoading(false);
};

const filteredPI = piData.filter(r =>
    !piFilter || r.payer_name.toLowerCase().includes(piFilter.toLowerCase()) || r.loc.toLowerCase().includes(piFilter.toLowerCase())
  );

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
      Loading AR Intelligence...
    </div>
  );

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>AR Intelligence</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>
            {summary?.upload ? `Last upload: ${summary.upload.upload_date} — ${fmtN(summary.upload.total_claims)} claims` : 'No uploads yet'}
          </p>
        </div>
        <select value={facilityId} onChange={e => setFacilityId(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 600, background: '#fff', cursor: 'pointer' }}>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
        </select>
      </div>

      {/* SECTION 1 — Upload */}
      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        style={{
          border: '2px dashed #cbd5e1', borderRadius: 12, padding: 32,
          textAlign: 'center', marginBottom: 24, background: '#f8fafc',
          cursor: 'pointer'
        }}
        onClick={() => fileRef.current?.click()}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
        <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
          {uploading ? 'Processing...' : 'Drop your billing AR CSV here or click to upload'}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Weekly open AR export from your billing system</div>
        <input ref={fileRef} type="file" accept=".csv,text/csv,application/csv,text/plain" style={{ display: 'none' }}
          onChange={e => handleUpload(e.target.files[0])} />
        {uploadMsg && (
          <div style={{ marginTop: 12, fontSize: 13, color: uploadMsg.ok ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
            {uploadMsg.text}
          </div>
        )}
      </div>

      {!summary?.upload && (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>
          Upload your first AR CSV to get started
        </div>
      )}

      {summary?.upload && (<>

        {/* SECTION 2 — KPI Banner */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total Open AR', value: fmt$(summary.kpis?.total_billed), sub: `${fmtN(summary.kpis?.total_claims)} claims`, color: '#1e40af' },
            { label: 'Estimated Collectible', value: fmt$(summary.kpis?.total_estimated_collectible), sub: 'Based on actual pay history', color: '#15803d' },
            { label: 'Action Required', value: fmtN(summary.kpis?.action_needed), sub: 'Appeals + escalated', color: '#dc2626' },
            { label: 'Oldest Claim', value: `${Math.max(...(summary.aging?.map(a => a.age_bucket === '120+' ? 121 : parseInt(a.age_bucket)) || [0]))}+ days`, sub: `${fmt$(summary.aging?.find(a => a.age_bucket === '120+')?.billed || 0)} at risk`, color: '#9333ea' },
          ].map((k, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', borderTop: `4px solid ${k.color}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{k.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* AI Analyze Button */}
        <div style={{ marginBottom: 24 }}>
          <button onClick={handleAiAnalyze} disabled={aiLoading} style={{
            background: aiLoading ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
            color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px',
            fontWeight: 700, fontSize: 14, cursor: aiLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            {aiLoading ? '⏳ Analyzing...' : '✨ AI Analyze This AR'}
          </button>

          {aiAnalysis && (
            <div style={{ marginTop: 16, background: 'linear-gradient(135deg, #1e1b4b, #312e81)', borderRadius: 12, padding: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
                ✨ AI Analysis
              </div>
              <div style={{ color: '#e0e7ff', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {aiAnalysis}
              </div>
            </div>
          )}
        </div>

        {/* Section Nav */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 }}>
          {[
            { id: 'action', label: '🚨 Action Required' },
            { id: 'aging', label: '📊 Aging Buckets' },
            { id: 'payer', label: '🏥 By Payer' },
            { id: 'intel', label: '🧠 Payer Intelligence' },
          ].map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
              background: 'none', border: 'none', padding: '10px 16px',
              fontWeight: activeSection === s.id ? 700 : 400,
              color: activeSection === s.id ? '#1e40af' : '#64748b',
              borderBottom: activeSection === s.id ? '2px solid #1e40af' : '2px solid transparent',
              cursor: 'pointer', fontSize: 13, marginBottom: -2,
            }}>
              {s.label}
            </button>
          ))}
        </div>

        {/* SECTION 3 — Action Required */}
        {activeSection === 'action' && (
          <div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              Claims requiring immediate attention — sorted by days outstanding
            </div>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Patient', 'Payer', 'LOC', 'Days Out', 'Billed', 'Est. Collectible', 'Status', 'Sub-Status'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(summary.actionItems || []).map((a, i) => {
                    const risk = a.days_outstanding > 120 ? '#fef2f2' : a.days_outstanding > 90 ? '#fffbeb' : '#fff';
                    return (
                      <tr key={i} style={{ background: risk, borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b' }}>{a.patient_last}, {a.patient_first}</td>
                        <td style={{ padding: '10px 14px', color: '#475569' }}>{a.payer_name}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ background: '#eff6ff', color: '#1e40af', borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 700 }}>{a.loc}</span>
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: a.days_outstanding > 120 ? '#dc2626' : a.days_outstanding > 90 ? '#d97706' : '#1e293b' }}>
                          {a.days_outstanding}d
                        </td>
                        <td style={{ padding: '10px 14px', color: '#475569' }}>{fmt$(a.billed_amount)}</td>
                        <td style={{ padding: '10px 14px', color: '#15803d', fontWeight: 600 }}>
                          {a.estimated_collectible ? fmt$(a.estimated_collectible) : <span style={{ color: '#94a3b8' }}>No history</span>}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{
                            background: a.claim_status === 'Escalated' ? '#fef2f2' : '#fffbeb',
                            color: a.claim_status === 'Escalated' ? '#dc2626' : '#92400e',
                            borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700
                          }}>{a.claim_status}</span>
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 12, maxWidth: 200 }}>{a.claim_sub_status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!summary.actionItems || summary.actionItems.length === 0) && (
                <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No action items found</div>
              )}
            </div>
          </div>
        )}

        {/* SECTION 4 — Aging */}
        {activeSection === 'aging' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
              {AGE_ORDER.map(bucket => {
                const row = summary.aging?.find(a => a.age_bucket === bucket) || {};
                const isRisk = bucket === '120+';
                return (
                  <div key={bucket} style={{ background: isRisk ? '#fef2f2' : '#fff', borderRadius: 12, padding: 20, border: `1px solid ${isRisk ? '#fecaca' : '#e2e8f0'}` }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: isRisk ? '#dc2626' : '#1e40af' }}>{bucket}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>days</div>
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}><strong>{fmtN(row.claims)}</strong> claims</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{fmt$(row.billed)}</div>
                    <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>Est: {fmt$(row.estimated_collectible)}</div>
                    {isRisk && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginTop: 8 }}>⚠️ Write-off risk</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SECTION 5 — By Payer */}
        {activeSection === 'payer' && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Payer', 'Claims', 'Billed', 'Est. Collectible', 'Avg Daily Rate', 'Risk'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(summary.byPayer || []).map((p, i) => {
                  const name = (p.payer_name || '').toUpperCase();
                  const isWriteoff = name.includes('NORTH CAROLINA') || name.includes('MEDICA') || name.includes('MODA');
                  const isSlowPay = name.includes('MOUNTAIN') || name.includes('TRICARE WEST');
                  const riskLabel = isWriteoff ? '⚫ Write-off risk' : isSlowPay ? '🔴 Slow pay' : '🟢 Normal';
                  const riskColor = isWriteoff ? '#dc2626' : isSlowPay ? '#d97706' : '#16a34a';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: isWriteoff ? '#fef2f2' : '#fff' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b' }}>{p.payer_name}</td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{fmtN(p.claims)}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{fmt$(p.billed)}</td>
                      <td style={{ padding: '10px 14px', color: '#15803d', fontWeight: 700 }}>
                        {parseFloat(p.estimated_collectible || 0) > 0 ? fmt$(p.estimated_collectible) : <span style={{ color: '#94a3b8' }}>No history</span>}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>
                        {p.avg_daily_rate ? fmt$(p.avg_daily_rate) + '/day' : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: riskColor, fontSize: 12 }}>{riskLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* SECTION 6 — Payer Intelligence */}
        {activeSection === 'intel' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <input
                value={piFilter}
                onChange={e => setPiFilter(e.target.value)}
                placeholder="Search payer or LOC..."
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, width: 280 }}
              />
              <span style={{ marginLeft: 12, fontSize: 12, color: '#94a3b8' }}>{filteredPI.length} payer/LOC combinations from 95,135 paid claims</span>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Payer', 'LOC', 'Avg Daily Rate (Actual)', 'Claim Days in History'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPI.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b' }}>{p.payer_name}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: '#eff6ff', color: '#1e40af', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{p.loc}</span>
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#15803d', fontSize: 15 }}>
                        {fmt$(p.avg_day_rate_collected)}<span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>/day</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#64748b' }}>{fmtN(p.claims_count)} days</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </>)}
    </div>
  );
}